import React, { useState, useEffect, useCallback } from 'react';
import './News.css'
import LoadingSpinner from '../../backend/components/LoadingSpinner'; // Import the loading spinner
import CardGrid from '../../components/card-grid';
import SubscribeForm from '../SubscribeFormFolder/SubscribeForm';

import { loadFull } from 'tsparticles';
import { loadPolygonMaskPlugin } from 'tsparticles-plugin-polygon-mask';
import ParticleBackground from '../../components/particlesbackground';
import type { Engine } from 'tsparticles-engine';

import {
    LazyLoadImage,
    LazyLoadComponent,
} from 'react-lazy-load-image-component';

// Assets
// Background video
const BannerBg = require('./videos/news-page-video.mp4');
const BannerBg_LazyLoadImg = require('./img//news-lazyload.png');

// Images
import newsBg1 from './img/news-bg1.png'

type Post = {
    title: string;
    href: string;
    date: string;
    datetime: string;
    image_url: any;
    new_tab: boolean;
    is_news: boolean;
    is_multimedia: boolean;
};

type MainPost = {
    title: string;
    href: string;
    date: string;
    datetime: string;
    image_url: any;
    description: string;
};

const News = () => {

    // Section for the video and its lazyLoad functionality
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    const [displayMode, setDisplayMode] = useState('all'); // Include 'videos' in the possible states

    const [posts, setPosts] = useState<Post[]>([]);
    const [mainPost, setMainPost] = useState<MainPost[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Particles Background
    const particlesInit = useCallback(async (main: Engine) => {
        await loadFull(main);
        await loadPolygonMaskPlugin(main);
    }, []);

    const [width, setWidth] = useState(window.innerWidth);
    const [height, setHeight] = useState(document.documentElement.scrollHeight);

    const [heightTopParticles, setHeightTopParticles] = useState<number>(0);

    useEffect(() => {
        if (!loading) {
            const handleResize = () => {
                const newWidth = window.innerWidth;
                const newHeight = document.documentElement.scrollHeight;
                setWidth(newWidth);
                setHeight(newHeight);
                setHeightTopParticles(newHeight * 0.36);
            };

            window.addEventListener('resize', handleResize);
            handleResize(); // Call once after the cards are loaded

            return () => window.removeEventListener('resize', handleResize);
        }
    }, [loading]); // Trigger when loading state changes


    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const response = await fetch('/api/posts/', {
                    method: 'GET',
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data: Post[] = await response.json();
                setPosts(data); // Assign the fetched posts to state
            } catch (err) {
                // Type assertion to 'Error' to access the message property
                if (err instanceof Error) {
                    setError(err.message); // Handle error
                } else {
                    setError('An unknown error occurred'); // Fallback for unknown errors
                }
            } finally {
                setLoading(false); // End loading state
            }
        };

        fetchPosts();
    }, []); // Empty dependency array to run once on mount


    const fetchMainPost = async () => {
        try {
            const response = await fetch('/api/highlightedpost/', {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data: MainPost[] = await response.json();
            setMainPost(data); // Assign the fetched posts to state
        } catch (err) {
            // Type assertion to 'Error' to access the message property
            if (err instanceof Error) {
                setError(err.message); // Handle error
            } else {
                setError('An unknown error occurred'); // Fallback for unknown errors
            }
        } finally {
            setLoading(false); // End loading state
        }
    };

    useEffect(() => {
        fetchMainPost();
    }, []); // Empty dependency array to run once on mount

    const handleButtonClick = (mode: any) => {
        setDisplayMode(displayMode === mode ? 'all' : mode);
    };

    // Filter posts based on the display mode
    const filteredPosts = (posts || []).filter((post) => {
        if (displayMode === 'all') return true;
        if (displayMode === 'news') return post.is_news;
        if (displayMode === 'stories') return !post.is_news && !post.is_multimedia;
        return post.is_multimedia; // Filter for multimedia posts (videos)
    });

    // Sort posts
    const sortedPosts = filteredPosts.sort(
        (a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime()
    );

    const getButtonClass = (mode: string) =>
        displayMode === mode ? 'btn custom-btn selected' : 'btn custom-btn';


    return (
        <div className="home-container">

            <div className="news-hero-section">
                {!isVideoLoaded && (
                    <LazyLoadComponent
                        placeholder={<img src={BannerBg_LazyLoadImg} alt="" />}
                    >
                        <LazyLoadImage
                            src={BannerBg_LazyLoadImg}
                            alt="="
                            className="hero-image3"
                        />
                    </LazyLoadComponent>
                )}

                <video
                    className={`hero-image3 ${isVideoLoaded ? '' : 'hidden'}`}
                    src={BannerBg}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onLoadedData={() => setIsVideoLoaded(true)}
                />

                <div className="dark-overlay"></div> {/* ← Overlay */}

                <div className="news-info-wrapper">
                    <h1>News & Media</h1>
                </div>
            </div>

            <div className='news-overall-wrapper'>
                {loading ? (
                    <LoadingSpinner />
                ) : mainPost && mainPost.length > 0 ? (
                    <div className='main-news-wrapper'>
                        <a
                            className="inside-main-news-image"
                            style={{
                                backgroundImage: `url(${mainPost[0].image_url})`,
                                cursor: 'pointer'
                            }}

                            href={mainPost[0].href}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <div>
                                <h1>{mainPost[0].title}</h1>
                                <p>
                                    {new Date(mainPost[0].datetime).toLocaleString('en-US', {
                                        timeZone: 'America/New_York',
                                        dateStyle: 'long'
                                    })}
                                </p>
                            </div>
                        </a>

                        <div className='post-info'>
                            <h1 className='news-overall-wrapper-h1' style={{ color: '#28AAE1', fontWeight: '700' }}>Top Story</h1>
                            <p>{mainPost[0].description}</p>
                        </div>
                    </div>
                ) : (
                    <p>No main post available.</p>
                )}


            </div>



            <div className='news-card-stack'>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '20px',
                    }}
                >
                    <button
                        className={getButtonClass('all')}
                        onClick={() => handleButtonClick('all')}
                        style={{ marginLeft: '0px' }}
                    >
                        All
                    </button>

                    <button
                        className={getButtonClass('news')}
                        onClick={() => handleButtonClick('news')}
                        style={{ marginLeft: '10px' }}
                    >
                        News
                    </button>
                    <button
                        className={getButtonClass('stories')}
                        onClick={() => handleButtonClick('stories')}
                        style={{ marginLeft: '10px' }}
                    >
                        Stories
                    </button>
                    <button
                        className={getButtonClass('multimedia')}
                        onClick={() => handleButtonClick('multimedia')}
                        style={{ marginLeft: '10px' }}
                    >
                        Multimedia
                    </button>
                </div>

                <div className="relative overflow-hidden w-100">

                    {/* Moved into conditional rendering */}
                    {!loading && posts.length > 0 && heightTopParticles !== 0 && (
                        <ParticleBackground
                            particlesInitBackground={particlesInit}
                            id="newsParticles"
                            number={Math.floor(heightTopParticles * 0.01)}
                            aspectRatio={width / heightTopParticles}
                        />
                    )}

                    {loading ? (<LoadingSpinner />) : (<CardGrid cards={sortedPosts} />)}
                </div>
            </div>

            <SubscribeForm />
        </div>
    )
}

export default News